import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { CsvExportUtil } from 'doctivity-shared/utils';

// Fixed row-count choices offered in the dialog. Capped at 1000 for now (and never
// above the per-export cap from CsvExportUtil).
const ROW_OPTIONS = [50, 100, 200, 500, 1000];
const DEFAULT_ROWS = 200;
const HARD_MAX_ROWS = 1000;
// Safety guard so a huge total doesn't build a runaway Start dropdown.
const MAX_START_OPTIONS = 1000;

/**
 * CSVExportDialog — global "choose your rows" dialog for CSV downloads, modeled on
 * PDFExportDialog. The user can rename the file, pick how many rows (a fixed set of
 * batch sizes), and pick which batch to start at (computed from total / rows). All row
 * counts are clamped to the per-export cap (CsvExportUtil) so a download can't exceed
 * the ~6 MB API Gateway response wall.
 *
 * On confirm it calls onConfirm({ limit, offset, fileName }) with a 0-based offset. It
 * does NOT fetch or build the CSV itself — the caller does, so it stays reusable.
 */
const styles = (theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  field: {
    marginTop: theme.spacing(2),
  },
  help: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
});

class CSVExportDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState(props);
  }

  componentDidUpdate(prevProps) {
    // Re-seed when the dialog is (re)opened or the export it targets changes.
    if (
      (this.props.open && !prevProps.open) ||
      this.props.exportType !== prevProps.exportType ||
      this.props.totalCount !== prevProps.totalCount ||
      this.props.defaultFileName !== prevProps.defaultFileName
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.initialState(this.props));
    }
  }

  initialState(props) {
    const options = this.rowOptions(props);
    // Preselect defaultRows when it's a valid option, else DEFAULT_ROWS, else the largest.
    const preferred = Number.isFinite(props.defaultRows) ? props.defaultRows : DEFAULT_ROWS;
    const rows = options.includes(preferred)
      ? preferred
      : options.includes(DEFAULT_ROWS)
        ? DEFAULT_ROWS
        : options[options.length - 1] || DEFAULT_ROWS;
    return {
      fileName: props.defaultFileName || 'doctivity_export.csv',
      rows,
      offset: 0,
    };
  }

  // Row-count choices for this export: the fixed set, capped at 1000 and at the
  // per-export cap.
  rowOptions(props = this.props) {
    const cap = Math.min(
      CsvExportUtil.getRowCap(props.exportType),
      HARD_MAX_ROWS,
    );
    const options = ROW_OPTIONS.filter((n) => n <= cap);
    return options.length ? options : [cap];
  }

  // Start-row choices: one per batch of `rows`, covering the total. Value is the
  // 0-based offset; label is the 1-based row range.
  startOptions() {
    const { totalCount } = this.props;
    const { rows } = this.state;
    if (!Number.isFinite(totalCount) || totalCount <= rows) {
      return [{ offset: 0, label: `1${Number.isFinite(totalCount) ? `–${totalCount}` : ''}` }];
    }
    const pages = Math.min(Math.ceil(totalCount / rows), MAX_START_OPTIONS);
    const out = [];
    for (let p = 0; p < pages; p += 1) {
      const startRow = p * rows + 1;
      const endRow = Math.min((p + 1) * rows, totalCount);
      out.push({ offset: p * rows, label: `${startRow}–${endRow}` });
    }
    return out;
  }

  handleFileNameChange = (e) => {
    this.setState({ fileName: e.target.value });
  };

  handleRowsChange = (e) => {
    // Changing batch size invalidates the current start offset -> reset to first batch.
    this.setState({ rows: parseInt(e.target.value, 10), offset: 0 });
  };

  handleStartChange = (e) => {
    this.setState({ offset: parseInt(e.target.value, 10) });
  };

  handleDownload = () => {
    const { rows, offset } = this.state;
    let fileName = (this.state.fileName || '').trim() || 'doctivity_export.csv';
    if (!/\.csv$/i.test(fileName)) {
      fileName += '.csv';
    }
    this.props.onConfirm({ limit: rows, offset, fileName });
  };

  render() {
    const { classes, open, onClose, title, exportType, totalCount } = this.props;
    const isActivityNotes =
      exportType === 'ProviderComments' ||
      exportType === 'ActivityNotes' ||
      exportType === 'ActivityNotesDownload';
    const rowOptions = this.rowOptions();
    const startOptions = this.startOptions();
    // Guard against a stale offset that isn't a valid start for the current batch size.
    const offsetValue = startOptions.some((o) => o.offset === this.state.offset)
      ? this.state.offset
      : 0;

    return (
      <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
        <DialogTitle className={classes.title}>
          {title || 'Download CSV'}
          <IconButton onClick={onClose} size='small' aria-label='close'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label='File name'
            fullWidth
            value={this.state.fileName}
            onChange={this.handleFileNameChange}
            helperText='.csv is added automatically if omitted.'
          />
          {Number.isFinite(totalCount) && (
            <Typography className={classes.field} variant='body2' color='textSecondary'>
              {totalCount.toLocaleString()} row{totalCount === 1 ? '' : 's'} available.
            </Typography>
          )}
          <TextField
            className={classes.field}
            select
            label='Number of rows'
            fullWidth
            value={this.state.rows}
            onChange={this.handleRowsChange}
          >
            {rowOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n.toLocaleString()}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.field}
            select
            label='Start at rows'
            fullWidth
            value={offsetValue}
            onChange={this.handleStartChange}
            disabled={startOptions.length <= 1}
          >
            {startOptions.map((o) => (
              <MenuItem key={o.offset} value={o.offset}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <Box className={classes.help}>
            <Typography variant='caption'>
              {isActivityNotes
                ? 'Activity Notes are capped lower because each note can contain a long write-up.'
                : 'Large exports are capped to keep the download from failing.'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={this.handleDownload}
            variant='contained'
            color='primary'
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

CSVExportDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Called with { limit, offset, fileName } (offset is 0-based) when the user confirms.
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  // Model / handler name used to look up the row cap (e.g. 'Providers', 'ProviderComments').
  exportType: PropTypes.string,
  // Total rows available, if known — shown to the user and used to build the Start options.
  totalCount: PropTypes.number,
  // Initial (editable) file name for the download.
  defaultFileName: PropTypes.string,
  // Preselected row count (must be one of the offered options, else falls back to 200).
  defaultRows: PropTypes.number,
};

const styled = withStyles(styles)(CSVExportDialog);
export { styled as CSVExportDialog };
export default styled;
